<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useFormat } from '@/composables/useFormat'
import { usePrinterStore } from '@/stores/printer'
import { useAbility } from '@casl/vue'
import { storeToRefs } from 'pinia'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Printer', // sesuaikan dengan subject CASL yang dipakai backend untuk modul ini
  },
})

const ability = useAbility()
const { formatRupiah, formatDate } = useFormat()
const { request, loading, errors } = useApiRequest()

const printerStore = usePrinterStore()
const {
  printers,
  loadingPrinters,
  pendingJobs,
  failedJobs,
  isProcessing,
  isBluetoothSupported,
} = storeToRefs(printerStore)

// ---------- Stasiun (untuk dropdown assign) ----------
const stations = ref([])
const fetchStations = async () => {
  const response = await useApi('/api/v1/stations').get()
  const data = response?.data ?? response
  stations.value = data ?? []
}

// ---------- Dialog: pairing printer baru ----------
const pairDialog = ref(false)
const pairForm = ref({ name: '', paperWidth: '58mm', assignedStationId: null })
const pairError = ref('')

const openPairDialog = () => {
  pairForm.value = { name: '', paperWidth: '58mm', assignedStationId: null }
  pairError.value = ''
  pairDialog.value = true
}

const submitPairing = async () => {
  pairError.value = ''
  try {
    await printerStore.pairPrinter({
      name: pairForm.value.name,
      paperWidth: pairForm.value.paperWidth,
      assignedStationId: pairForm.value.assignedStationId,
    })
    pairDialog.value = false
  } catch (e) {
    // Web Bluetooth API melempar error native (mis. user batal pilih device),
    // ditampilkan langsung di dialog agar kasir tahu kenapa gagal.
    pairError.value = e?.message ?? 'Gagal pairing printer.'
  }
}

// ---------- Dialog: tambah printer manual (network/usb) ----------
const manualDialog = ref(false)
const manualForm = ref({
  name: '',
  type: 'network',
  device_identifier: '',
  paper_width: '58mm',
  assigned_station_id: null,
  is_active: true,
})

const openManualDialog = () => {
  manualForm.value = {
    name: '', type: 'network', device_identifier: '',
    paper_width: '58mm', assigned_station_id: null, is_active: true,
  }
  manualDialog.value = true
}

const submitManual = async () => {
  const res = await request(() => printerStore.createPrinterRecord(manualForm.value))
  if (res) manualDialog.value = false
}

// ---------- Edit printer ----------
const editDialog = ref(false)
const editForm = ref({ id: null, name: '', paper_width: '58mm', assigned_station_id: null, is_active: true })

const openEditDialog = printer => {
  editForm.value = {
    id: printer.id,
    name: printer.name,
    paper_width: printer.paper_width,
    assigned_station_id: printer.assigned_station_id,
    is_active: printer.is_active,
  }
  editDialog.value = true
}

const submitEdit = async () => {
  const res = await request(() => printerStore.updatePrinterRecord(editForm.value.id, {
    name: editForm.value.name,
    paper_width: editForm.value.paper_width,
    assigned_station_id: editForm.value.assigned_station_id,
    is_active: editForm.value.is_active,
  }))
  if (res) editDialog.value = false
}

const setAsDefaultReceipt = async printer => {
  await request(() => printerStore.updatePrinterRecord(printer.id, { is_default_receipt_printer: true }))
}

// ---------- Hapus printer ----------
const deleteDialog = ref(false)
const selectedPrinter = ref(null)

const confirmDelete = printer => {
  selectedPrinter.value = printer
  deleteDialog.value = true
}

const handleDeleteConfirm = async isConfirmed => {
  if (!isConfirmed || !selectedPrinter.value) return
  await request(() => printerStore.deletePrinterRecord(selectedPrinter.value.id))
}

// ---------- Init ----------
onMounted(async () => {
  await Promise.all([
    printerStore.fetchPrinters(),
    fetchStations(),
    printerStore.loadQueueFromDexie(),
  ])
  await printerStore.restorePairedDevices()  // auto-reconnect diam-diam kalau browser support
  await printerStore.syncPendingFromServer() // tangkap job pending dari sesi/device lain
})

const typeLabel = { bluetooth: 'Bluetooth', network: 'Jaringan (LAN)', usb: 'USB' }
const jobTypeLabel = { receipt: 'Struk', kitchen_ticket: 'Tiket Dapur' }
const statusColor = { pending: 'warning', waiting_device: 'warning', printing: 'info', success: 'success', failed: 'error' }
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">
          Printer Engine
        </h4>
        <p class="text-body-2 text-medium-emphasis">
          Kelola printer struk & dapur, pairing Bluetooth, dan pantau antrian cetak.
        </p>
      </VCol>
    </VRow>

    <VAlert
      v-if="!isBluetoothSupported"
      type="warning"
      variant="tonal"
      class="mb-4"
    >
      Browser ini tidak mendukung Web Bluetooth API. Pairing printer Bluetooth hanya bisa dilakukan lewat
      Chrome/Edge (Android atau Desktop). Printer jaringan/USB tetap bisa didaftarkan manual.
    </VAlert>

    <!-- ===================== REGISTRY PRINTER ===================== -->
    <VCard class="mb-6">
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <h6 class="text-h6">
          Daftar Printer
        </h6>
        <VSpacer />

        <VBtn
          v-if="ability.can('create', 'Printer') && isBluetoothSupported"
          color="primary"
          variant="tonal"
          prepend-icon="ri-bluetooth-line"
          @click="openPairDialog"
        >
          Pairing Bluetooth
        </VBtn>

        <VBtn
          v-if="ability.can('create', 'Printer')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openManualDialog"
        >
          Tambah Manual
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTable
        :headers="[
          { title: 'Nama', key: 'name' },
          { title: 'Tipe', key: 'type' },
          { title: 'Kertas', key: 'paper_width' },
          { title: 'Stasiun', key: 'assigned_station_id' },
          { title: 'Default Struk', key: 'is_default_receipt_printer' },
          { title: 'Status', key: 'is_active' },
          { title: 'Aksi', key: 'actions', sortable: false, width: '160px' },
        ]"
        :items="printers"
        :loading="loadingPrinters"
        hover
      >
        <template #item.type="{ item }">
          {{ typeLabel[item.type] ?? item.type }}
        </template>

        <template #item.assigned_station_id="{ item }">
          {{ stations.find(s => s.id === item.assigned_station_id)?.name ?? '-' }}
        </template>

        <template #item.is_default_receipt_printer="{ item }">
          <VChip
            v-if="item.is_default_receipt_printer"
            color="success"
            size="small"
            variant="tonal"
          >
            Default
          </VChip>
          <VBtn
            v-else-if="ability.can('update', 'Printer')"
            size="small"
            variant="text"
            @click="setAsDefaultReceipt(item)"
          >
            Jadikan Default
          </VBtn>
        </template>

        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Printer')"
            icon
            size="small"
            variant="text"
            @click="openEditDialog(item)"
          >
            <VIcon icon="ri-edit-line" />
          </VBtn>
          <VBtn
            v-if="ability.can('delete', 'Printer')"
            icon
            size="small"
            variant="text"
            color="error"
            @click="confirmDelete(item)"
          >
            <VIcon icon="ri-delete-bin-line" />
          </VBtn>
        </template>

        <template #no-data>
          <div class="text-center pa-8">
            <VIcon
              icon="ri-printer-line"
              size="64"
              color="grey-lighten-1"
            />
            <p class="text-body-1 mt-4">
              Belum ada printer terdaftar
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- ===================== MONITOR ANTRIAN CETAK ===================== -->
    <VCard>
      <VCardText class="d-flex align-center gap-3">
        <h6 class="text-h6">
          Antrian Cetak
        </h6>
        <VChip
          size="small"
          :color="isProcessing ? 'info' : 'default'"
          variant="tonal"
        >
          {{ isProcessing ? 'Memproses...' : 'Idle' }}
        </VChip>
        <VSpacer />
        <VBtn
          size="small"
          variant="text"
          prepend-icon="ri-refresh-line"
          @click="printerStore.syncPendingFromServer()"
        >
          Sinkronkan
        </VBtn>
      </VCardText>

      <VDivider />

      <VCardText v-if="pendingJobs.length === 0 && failedJobs.length === 0">
        <div class="text-center pa-4 text-medium-emphasis">
          Tidak ada job cetak yang mengantre. Semua struk/tiket sudah tercetak.
        </div>
      </VCardText>

      <VList v-else>
        <VListItem
          v-for="job in [...failedJobs, ...pendingJobs]"
          :key="job.localId"
        >
          <template #prepend>
            <VIcon
              :icon="job.jobType === 'receipt' ? 'ri-receipt-line' : 'ri-restaurant-line'"
              class="me-2"
            />
          </template>

          <VListItemTitle>
            {{ jobTypeLabel[job.jobType] ?? job.jobType }} — {{ job.idempotencyKey.slice(0, 8) }}
          </VListItemTitle>
          <VListItemSubtitle v-if="job.lastError">
            {{ job.lastError }}
          </VListItemSubtitle>

          <template #append>
            <VChip
              size="small"
              :color="statusColor[job.status] ?? 'default'"
              variant="tonal"
              class="me-2"
            >
              {{ job.status === 'waiting_device' ? 'Menunggu printer' : job.status }}
            </VChip>
            <VBtn
              v-if="job.status === 'failed'"
              size="small"
              variant="text"
              @click="printerStore.retryJob(job.localId)"
            >
              Coba Lagi
            </VBtn>
          </template>
        </VListItem>
      </VList>
    </VCard>

    <!-- ===================== DIALOG: PAIRING BLUETOOTH ===================== -->
    <VDialog
      v-model="pairDialog"
      max-width="480"
    >
      <VCard>
        <VCardTitle>Pairing Printer Bluetooth</VCardTitle>
        <VCardText>
          <VAlert
            v-if="pairError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ pairError }}
          </VAlert>

          <p class="text-body-2 mb-4">
            Pastikan printer sudah menyala dan dalam mode pairing. Browser akan menampilkan
            daftar device Bluetooth di sekitar setelah tombol di bawah diklik.
          </p>

          <VTextField
            v-model="pairForm.name"
            label="Nama Printer"
            placeholder="Printer Kasir 1"
            class="mb-4"
          />

          <VSelect
            v-model="pairForm.paperWidth"
            label="Lebar Kertas"
            :items="[{ title: '58mm', value: '58mm' }, { title: '80mm', value: '80mm' }]"
            class="mb-4"
          />

          <VSelect
            v-model="pairForm.assignedStationId"
            label="Assign ke Stasiun (opsional)"
            clearable
            :items="stations.map(s => ({ title: s.name, value: s.id }))"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="pairDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            prepend-icon="ri-bluetooth-line"
            @click="submitPairing"
          >
            Mulai Pairing
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ===================== DIALOG: TAMBAH MANUAL (network/usb) ===================== -->
    <VDialog
      v-model="manualDialog"
      max-width="480"
    >
      <VCard>
        <VCardTitle>Tambah Printer Manual</VCardTitle>
        <VCardText>
          <VTextField
            v-model="manualForm.name"
            label="Nama Printer"
            :error-messages="errors.name"
            class="mb-4"
          />
          <VSelect
            v-model="manualForm.type"
            label="Tipe"
            :items="[{ title: 'Jaringan (LAN)', value: 'network' }, { title: 'USB', value: 'usb' }]"
            class="mb-4"
          />
          <VTextField
            v-model="manualForm.device_identifier"
            :label="manualForm.type === 'network' ? 'IP:Port' : 'Device Path/ID'"
            placeholder="192.168.1.50:9100"
            :error-messages="errors.device_identifier"
            class="mb-4"
          />
          <VSelect
            v-model="manualForm.paper_width"
            label="Lebar Kertas"
            :items="[{ title: '58mm', value: '58mm' }, { title: '80mm', value: '80mm' }]"
            class="mb-4"
          />
          <VSelect
            v-model="manualForm.assigned_station_id"
            label="Assign ke Stasiun (opsional)"
            clearable
            :items="stations.map(s => ({ title: s.name, value: s.id }))"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="manualDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="loading"
            @click="submitManual"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ===================== DIALOG: EDIT PRINTER ===================== -->
    <VDialog
      v-model="editDialog"
      max-width="480"
    >
      <VCard>
        <VCardTitle>Edit Printer</VCardTitle>
        <VCardText>
          <VTextField
            v-model="editForm.name"
            label="Nama Printer"
            :error-messages="errors.name"
            class="mb-4"
          />
          <VSelect
            v-model="editForm.paper_width"
            label="Lebar Kertas"
            :items="[{ title: '58mm', value: '58mm' }, { title: '80mm', value: '80mm' }]"
            class="mb-4"
          />
          <VSelect
            v-model="editForm.assigned_station_id"
            label="Assign ke Stasiun"
            clearable
            :items="stations.map(s => ({ title: s.name, value: s.id }))"
            class="mb-4"
          />
          <VSwitch
            v-model="editForm.is_active"
            label="Aktif"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="editDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="loading"
            @click="submitEdit"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ===================== KONFIRMASI HAPUS ===================== -->
    <!-- Sesuaikan nama prop/emit di bawah dengan ConfirmDialog.vue project Anda jika berbeda -->
    <ConfirmDialog
      v-model:is-dialog-visible="deleteDialog"
      confirm-title="Hapus Printer?"
      :confirmation-question="`Apakah Anda yakin ingin menghapus printer ${selectedPrinter?.name}? Print job lama tetap tersimpan untuk audit.`"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>
